import { Injectable } from "@angular/core";
import { LeadDatetime } from '../model/leaddatetime.interface';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { Calendar } from '@ionic-native/calendar/ngx';
import { ICalendarEvent } from '../model/calendarevent.interface';
import { FirebaseX } from "@ionic-native/firebase-x/ngx";
import { Client } from '../model/leadClients/lead.client.model';
import { Platform } from '@ionic/angular';
import { LeadClientsGroup } from "../model/leadClients/lead.clients.group.model";
import * as Sentry from "@sentry/angular"
import { environment } from 'src/environments/environment';
import { DEV_ENV_NAME } from '../constants/env';
import { IErrorDetails } from '../model/errordetails.interface';
import { claimBackendError } from '../error-handler/backend-error';
import { isConnectivityFailure, serverAnswerStatus } from '../error-handler/connectivity';

/**
 * Error carrying the fault number that was shown to the user, so the global error
 * handler can attach it to the Sentry event as a searchable tag.
 */
export interface IFaultError extends Error {
    faultNumber?: string;
    companyId?: number;
    userId?: number;
    backendErrorKey?: string;
    sentryTags?: Record<string, string>;
}

/** Above this, a request is reported to Sentry as slow. See AppService.logTimeToSentry. */
const SLOW_REQUEST_THRESHOLD_MS = 5000;

/**
 * The API's answer to a deadlock that outlived its own retries, or to an insert that lost a race
 * (SqlErrorClassifier in marketing): a conflict it expects and asks the user to retry, not a fault.
 */
export const CONFLICT = 409;

@Injectable()
export class AppService {
    public menuClass = "";
    public networkstatus = "";
    public networkstatusClass = "";
    public networkstatusTimer: any = null;
    public error = "";
    public errorActive = false;
    public errorDetails: IErrorDetails | null = null;
    public downloadPopupActive = false;
    public introPopupActive = false;
    public permissionPopupActive = false;
    public ieWarningPopupActive = false;
    public versionUpdatePopupActive = false;
    public readonly tooltipPopupClosed = new Subject<void>();
    private tooltipPopupActiveValue = false;
    public textOfTooltipPopup = "";
    public titleOfTooltipPopup = "";
    public isBusy = false;

    public get tooltipPopupActive(): boolean { return this.tooltipPopupActiveValue; }
    public set tooltipPopupActive(value: boolean) {
        const closed = this.tooltipPopupActiveValue && !value;
        this.tooltipPopupActiveValue = value;
        if (closed) {
            this.tooltipPopupClosed.next();
        }
    }

    public leadClients: Client[] = [];
    public leadClientsGroup: LeadClientsGroup[] = [];
    public sorting: string = "lastMessageDate";
    public dir: number = 1;
    public processId: number = null;
    public isLoadingLeadCLients = false;

    public statusesClass = "";
    public userClass = "";

    public currentFilter: number = 0;

    // for overlays
    public statusesActive: boolean = false;
    public calendarActive: boolean = false;

    public needResyncLeadClient: boolean = false;

    // Pushed in by AuthService: AppService cannot inject it back without a circular dependency.
    private currentUserId: number | null = null;
    private currentCompanyId: number | null = null;

    constructor(
        private platform: Platform,
        private router: Router,
        private calendar: Calendar,
        private readonly firebaseX: FirebaseX
    ) { }

    get isMobile(): boolean { return this.platform.is("mobile"); }
    public range(a: number, b: number): number[] {
        let res: number[] = [];

        for (let i: number = a; i <= b; i++) {
            res.push(i);
        }

        return res;
    }

    public twoDigits(n: number): string {
        return (n < 10) ? `0${n}` : `${n}`;
    }

    public showNetworkstatus(s: string): void {
        if (this.networkstatusTimer) {
            clearTimeout(this.networkstatusTimer);
        }

        this.networkstatus = s;
        this.networkstatusClass = "active";
        this.networkstatusTimer = setTimeout(() => {
            this.networkstatusClass = "";
            this.networkstatusTimer = null;
        }, 2000);
    }

    public showError(errType: string, errMsg: any, isServiceNotAvailable: boolean = false, responseCode: number = undefined, throwException = true,
                throwErrorMsg: string = null, sentryTags: Record<string, string> = null): void {
        // Normalize error message to string
        let normalizedMsg: string = this.getErrorMessage(errMsg);

        const unauthorized: boolean = !!normalizedMsg && (
            normalizedMsg === "User not found"
                || normalizedMsg === "code: 404, error: User not found"
                || normalizedMsg.includes("- token filter error:")
                || responseCode === 401
        );
        // One user action can fail several requests (opening a missing lead client 404s
        // both the client and its notes), and each would mint its own number while the
        // SDK's dedupe keeps only the first event. Reuse the number already on screen so
        // whichever event survives carries the id the user can actually see and report.
        const uuid = (this.errorActive && this.errorDetails) ? this.errorDetails.faultNumber : this.uuid();
        normalizedMsg = normalizedMsg.replace("{FAULT_NUMBER}", uuid);
        // A request that never reached the server is never an application error, whatever the
        // caller passed: the flag is hand-written at every call site, so the same unreachable
        // server showed the full-screen "application error" from one repository and the soft
        // "server not available" notice from the next one (#336).
        const serviceNotAvailable = isServiceNotAvailable || isConnectivityFailure(normalizedMsg);
        // A conflict gets the soft notice and is not reported: there is nothing to fix, the user retries. So no fault
        // number on it either - it would match nothing in Sentry (#376).
        const conflict = responseCode === CONFLICT;
        const details: IErrorDetails = {
            faultNumber: uuid,
            message: normalizedMsg,
            time: this.formatErrorTime(new Date()),
            companyId: this.currentCompanyId,
            userId: this.currentUserId,
            version: environment.version,
            platform: this.describePlatform(),
            url: window.location.href,
        };
        if (!serviceNotAvailable && !conflict) {
            this.error = `${uuid} ${normalizedMsg}`;
            this.errorDetails = details;
            this.errorActive = true;
            let description = `url:${details.url}, id:${uuid}, type:${errType}, msg:${normalizedMsg}`
                + `, time:${details.time}, companyId:${details.companyId}, userId:${details.userId}`
                + `, version:${details.version}, platform:${details.platform}`;

            if (this.platform.is("cordova") && !unauthorized) {
                this.firebaseX.logError(description); // crashlytics
            }
        } else {
            this.tooltipPopupActive = true;
            // The number goes into the soft notice too: support has nothing else to look the
            // report up by, and this branch now also takes the errors rerouted above.
            this.textOfTooltipPopup = conflict ? normalizedMsg : `${normalizedMsg} (${uuid})`;
            this.titleOfTooltipPopup = errType;
        }
        if (unauthorized) {
            this.router.navigateByUrl("/auth/logout");
        } else if (throwException && !conflict) {
            // The fault number rides on the error so the global handler can tag the Sentry
            // event with it: that is what makes the number in the user's screenshot findable.
            const faultError: IFaultError = new Error(throwErrorMsg || `${errType} - ${normalizedMsg}`);
            faultError.faultNumber = uuid;
            faultError.companyId = details.companyId;
            faultError.userId = details.userId;
            // Group by the error itself, not by the localized wrapper around it, and take
            // the message over from the interceptor: this event is the richer one, it
            // carries the fault number (#320).
            faultError.backendErrorKey = claimBackendError(normalizedMsg);
            faultError.sentryTags = sentryTags;
            throw faultError;
        }
    }

    /**
     * The error branch of a request. Callers used to report every failure there as "server not
     * available, check your internet connection", answers the server did give included - and their
     * own text reached neither the screen nor Sentry (#336). An answer is an API error like any other.
     */
    public showHttpError(err: unknown, unavailableMsg: string): void {
        const status = serverAnswerStatus(err);
        if (status) {
            this.showError("HTTP error", `code: ${status}, error: ${this.getErrorMessage(err)}`);
        } else {
            this.showError("HTTP error", unavailableMsg, true);
        }
    }

    public hideError() {
        this.error = "";
        this.errorDetails = null;
        this.errorActive = false;
    }

    /**
     * Escape hatch for a lead client page that can never render: its skeleton stays on
     * screen until the client loads, so a client the API refuses (deleted, or outside the
     * session's company scope) leaves the user on a blank screen with no way forward. Most
     * often reached from a push deep link, where the id is not one the user picked (#328).
     */
    public goToLeadClients(): void {
        this.router.navigateByUrl("/leadClients");
    }

    /**
     * Identity for error reports. Pushed in by AuthService on login/restore/logout.
     * User fields are optional and the user may be null, so accept both.
     */
    public setCurrentIdentity(userId?: number | null, companyId?: number | null): void {
        this.currentUserId = userId || null;
        this.currentCompanyId = companyId || null;
    }

    /**
     * Local time with an explicit UTC offset — server logs are UTC, users are not.
     */
    private formatErrorTime(date: Date): string {
        const offsetMinutes = -date.getTimezoneOffset();
        const sign = offsetMinutes < 0 ? "-" : "+";
        const absOffset = Math.abs(offsetMinutes);
        const offset = `${sign}${this.twoDigits(Math.floor(absOffset / 60))}:${this.twoDigits(absOffset % 60)}`;
        return `${date.getFullYear()}-${this.twoDigits(date.getMonth() + 1)}-${this.twoDigits(date.getDate())}`
            + ` ${this.twoDigits(date.getHours())}:${this.twoDigits(date.getMinutes())}:${this.twoDigits(date.getSeconds())}`
            + ` UTC${offset}`;
    }

    private describePlatform(): string {
        if (!this.platform.is("cordova")) {
            return "web";
        }
        if (this.platform.is("ios")) {
            return "app/ios";
        }
        if (this.platform.is("android")) {
            return "app/android";
        }
        return "app";
    }

    public getErrorMessage(error: unknown) {
        if (error instanceof Error) {
            return error.message;
        } else if (typeof error === 'object') {
            return JSON.stringify(error);
        } else {
            return String(error);
        }
    }

    public initDownloadPopup(): void {
        if (!localStorage.getItem("lastPopup")) {
            localStorage.setItem("lastPopup", "0");
        }

        setInterval(() => {
            var lastPopup = parseInt(localStorage.getItem("lastPopup"));
            var now = (new Date()).getTime();

            if (now - lastPopup > 1000 * 60 * 60 * 24 * 5) { // once per 5 days            
                this.downloadPopupActive = true;
                localStorage.setItem("lastPopup", now.toString());
            }
        }, 5000);
    }

    public initPermissionPopup(): void {
        if (!localStorage.getItem("lastPopup1")) {
            localStorage.setItem("lastPopup1", "0");
        }

        setInterval(() => {
            var lastPopup = parseInt(localStorage.getItem("lastPopup1"));
            var now = (new Date()).getTime();
            if (now - lastPopup > 1000 * 60 * 60 * 24 * 5 && this.platform.is("cordova")) { // once per 5 days
                this.permissionPopupActive = true;
                localStorage.setItem("lastPopup1", now.toString());
            }
        }, 2000);
    }

    public initVersionUpdatePopup() {
        setInterval(() => {
            if (!localStorage.getItem("versionUpdatePopup")) {
                localStorage.setItem("versionUpdatePopup", "0");
            }

            var lastPopup = parseInt(localStorage.getItem("versionUpdatePopup"));
            var now = (new Date()).getTime();
            if (now - lastPopup > 1000 * 60 * 60 * 24 && this.platform.is("cordova")) { // once per 5 days
                this.versionUpdatePopupActive = true;
                localStorage.setItem("versionUpdatePopup", now.toString());
            }
        }, 2000);
    }

    public initIntroPopup(): void {
        if (!localStorage.getItem("notfirstrun")) {
            localStorage.setItem("notfirstrun", "1");
            this.introPopupActive = true;
        }
    }

    public uuid(): string {
        return Array
            .from(Array(32))
            .map((e, i) => {
                let someRandomValue = i === 12 ? 4 : (+new Date() + Math.random() * 16) % 16 | 0;
                return `${~[8, 12, 16, 20].indexOf(i) ? "-" : ""}${(i === 16 ? someRandomValue & 0x3 | 0x8 : someRandomValue).toString(16)}`;
            })
            .join("");
    }

    public leaddatetime2date(ldt: LeadDatetime): Date {
        if (Array.isArray(ldt)) {
            const year = ldt[0].year.toString();
            const month = this.twoDigits(ldt[0].month);
            const day = this.twoDigits(ldt[0].day);
            const hour = this.twoDigits(ldt[0].hour);
            const minute = this.twoDigits(ldt[0].minute);

            return new Date(`${year}/${month}/${day} ${hour}:${minute}:00`);
        }

        const year = ldt.year.toString();
        const month = this.twoDigits(ldt.month);
        const day = this.twoDigits(ldt.day);
        const hour = this.twoDigits(ldt.hour);
        const minute = this.twoDigits(ldt.minute);

        return new Date(`${year}/${month}/${day} ${hour}:${minute}:00`);
    }

    // raw Date to dd/mm/yyyy
    public fulldate2date(date: Date): string {
        return `${date.getDate()}/${this.twoDigits(date.getMonth() + 1)}/${date.getFullYear()}`;
    }

    public async SetCalendarEvent(leadCLient: Client, schema: string, subject: string,
                                  start: Date = leadCLient.rawFollowup): Promise<boolean> {
        const eventName = this.buildEventName(leadCLient, schema);
        const eventNotes = `lead id: ${leadCLient.id}`;
        // The meeting screen sends "" when no subject was typed: the event was written untitled, "(No title)" in Google (#385).
        const eventTitle = subject || eventName;

        if (await this.getCalendarPermissions()) {
            const endDate = new Date(start);
            endDate.setHours(start.getHours() + 1);

            const existingEvents: ICalendarEvent[] = await this.calendar.findEvent(eventTitle, "", eventNotes, start, endDate);

            if (existingEvents.length) {
                for (const ev of existingEvents) {
                    // fromDate is positional in the plugin: without it a successful delete calls reject(null)
                    this.calendar.deleteEventById(ev.id, null).catch(e => this.onCalendarDeleteFailed(e));
                }
            }

            // The lead id stays the first line: the app finds its events by it. The customer's details follow it.
            await this.calendar.createEvent(eventTitle, "", `${eventNotes}\n${eventName}`, start, endDate);
            return true;
        }

        return false;
    }

    // Replaces the event instead of modifying it: Android has no modifyEvent, and the subject may change with the time.
    public async ModifyCalendarEvent(leadClient: Client, schema: string, oldStart: Date, newStart: Date,
                                     subject: string): Promise<boolean> {
        const eventNotes = `lead id: ${leadClient.id}`;

        if (await this.getCalendarPermissions()) {
            // The minute after the start, not the whole hour: Android also returns events touching the window edges,
            // which would catch the lead's back-to-back meetings.
            const from = new Date(oldStart.getTime() + 60000);
            const to = new Date(oldStart.getTime() + 120000);

            // Any title, exact first line of the notes: the plugin matches "contains", so "lead id: 1" would also catch
            // "lead id: 12". Events written before #385 have that line alone.
            const oldEvents: ICalendarEvent[] = await this.calendar.findEvent("", "", eventNotes, from, to);

            for (const ev of oldEvents.filter(e => (e.message || "").split("\n")[0] === eventNotes)) {
                await this.calendar.deleteEventById(ev.id, null).catch(e => this.onCalendarDeleteFailed(e));
            }

            return this.SetCalendarEvent(leadClient, schema, subject, newStart);
        }

        return false;
    }

    public async DeleteCalendarEvent(leadClient: Client, schema: string, startDate: Date, subject: string) {
        let eventName = this.buildEventName(leadClient, schema);
        let eventNotes = `lead id: ${leadClient.id}`;
        var eventTitle = subject == null ? eventName : subject;

        if (await this.getCalendarPermissions()) {
            startDate.setHours(startDate.getHours());
            let endDate = new Date(startDate);
            endDate.setHours(startDate.getHours() + 1);

            await this.calendar.deleteEvent(eventTitle, "", eventNotes, startDate, endDate).catch(e => this.onCalendarDeleteFailed(e));
        }
    }

    // A delete wants the event gone, so iOS's "already gone" answers ("Object not found. It may have been deleted."
    // from deleteEvent, "Could not find event." from deleteEventById) are success. Android answers false instead (#388).
    private onCalendarDeleteFailed(error: unknown): void {
        const text = String(error);
        if (!/not found|not find/i.test(text)) {
            this.logToSentry("Calendar event delete failed", { error: text });
        }
    }

    private async getCalendarPermissions(): Promise<boolean> {
        try {
            if (await this.calendar.hasReadWritePermission()) {
                return true;
            }

            await this.calendar.requestReadWritePermission();
            return await this.calendar.hasReadWritePermission();
        } catch {
            // Without calendar access the plugin rejects the request (with null on iOS) instead of resolving.
            return false;
        }
    }

    private buildEventName(leadCLient: Client, schema: string): string {
        return schema
            .replace("{fullname}", leadCLient.fullName || "")
            .replace("{phone}", leadCLient.phone || "")
            .replace("{email}", leadCLient.email || "")
            .replace("{createdate}", leadCLient.formatCreatedate)
            .replace("{followup}", leadCLient.formatCreatedate)
            .replace(/\n/g, " ")
            .replace(/\r/g, " ")
    }

    public formatText(s: string, rebuildSpaces: boolean = true): string {
        let res = s.trim().replace(/\n/g, "<br>");

        if (rebuildSpaces) {
            res = res.replace(/ /g, "&nbsp;");
        }

        return res;
    }

    public rebuildGroups(leadClients: Client[]): void {
        this.leadClientsGroup = [];
        leadClients.forEach(l => {
            let date: string = this.fulldate2date(new Date(l[this.sorting]));
            let existingLg: LeadClientsGroup | null = this.leadClientsGroup.find(lg => lg.date === date) || null;

            if (l.preview) {
                if (existingLg) {
                    let existingLeadClient: Client | null = existingLg.leadClients.find(x => x.id == l.id);
                    if (!existingLeadClient) {
                        existingLg.leadClients.push(l);
                    }
                } else {
                    let newLg: LeadClientsGroup = new LeadClientsGroup(date, [l]);
                    this.leadClientsGroup.push(newLg);
                }
            }
        });
    }

    public buildGroups(leadClients: Client[]): void {
        leadClients.filter(l => l[this.sorting] !== null).forEach(l => {
            let date: string = this.fulldate2date(new Date(l[this.sorting]));
            let existingLg: LeadClientsGroup | null = this.leadClientsGroup.find(lg => lg.date === date) || null;

            if (l.preview) {
                if (existingLg) {
                    let existingLeadClient: Client | null = existingLg.leadClients.find(x => x.id == l.id);
                    if (!existingLeadClient) {
                        existingLg.leadClients.push(l);
                    }
                } else {
                    let newLg: LeadClientsGroup = new LeadClientsGroup(date, [l]);
                    this.leadClientsGroup.push(newLg);
                }
            }
        });
    }

    public logToSentry(message: string, params?: Record<string, string>) {
        const newError = new Error(message);
        console.error("TSTERR logToSentry", newError, params ? JSON.stringify(params) : "");
        Sentry.withScope(scope => {
            scope.setExtra("errorExtraData", newError);
            scope.setExtra("handlerSource", "app-service");
            if (params) {
                for (const key in params) {
                    scope.setExtra(key, params[key]);
                }
            }
            const eventId = Sentry.captureException(newError);
        });
    }

    /**
     * Report a request that took longer than the threshold.
     *
     * Deliberately NOT routed through logToSentry (#319): that captures an exception at error level,
     * and a slow request is not a failure — it finished and the user saw the screen. Reported as an
     * error it outranked real failures in triage, 1713 events against the 143 of an endpoint that was
     * returning 500 to 22 users. On the error branch the failure itself is already reported: showError
     * throws a faultError that the global handler sends, so raising the level here would only duplicate
     * it. The two call sites stay apart through desc, which says whether the request also failed.
     *
     * The duration goes into a tag as a bucket, not into extra: Sentry can filter and aggregate on
     * tags, while an extra can only be read by opening events one at a time.
     */
    public logTimeToSentry(desc: string, startTime: number, endTime: number) {
        const timeDiff = endTime - startTime;
        if (timeDiff <= SLOW_REQUEST_THRESHOLD_MS || environment.envName === DEV_ENV_NAME) {
            return;
        }

        const message = `Request takes long time | ${desc}`;
        console.warn("TSTERR logTimeToSentry", message, Math.round(timeDiff));
        Sentry.withScope(scope => {
            scope.setLevel(Sentry.Severity.Warning);
            scope.setTag("slow_request", desc);
            scope.setTag("slow_bucket", AppService.slowRequestBucket(timeDiff));
            scope.setExtra("handlerSource", "app-service");
            scope.setExtra("durationMs", Math.round(timeDiff));
            Sentry.captureMessage(message);
        });
    }

    private static slowRequestBucket(timeDiff: number): string {
        if (timeDiff < 10000) {
            return "5-10s";
        }
        if (timeDiff < 20000) {
            return "10-20s";
        }

        return "20s+";
    }
}
